import type { ApolloCache, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import type { MutationResult } from '../../graphql';
import type { Dbx } from '../account-store';
import { QUERY } from '../use-sub-users-account';

interface TData {
  updateManySubUserPermissions: {
    result: {
      id: number;
      event_managment: boolean;
      view_and_download: boolean;
      content_managment: boolean;
      destinations: boolean;
      customize_webplayer: boolean;
      organization_account_info: boolean;
      activity_log: boolean;
      billing: boolean;
      admin_access: boolean;
      customize_production_notes: boolean;
      some_location_has_event_managment_enabled: boolean;
      some_location_has_view_and_download_enabled: boolean;
      some_location_has_content_managment_enabled: boolean;
      some_location_has_activity_log_enabled: boolean;
      some_location_has_customize_production_notes_enabled: boolean;
    }[];
  };
}

interface TVariables {
  clientId: number;
  input: PerissionsData[];
}

interface PerissionsData {
  destinations: boolean;
  customize_webplayer: boolean;
  billing: boolean;
  admin_access: boolean;
  organization_account_info: boolean;
  id?: number;
}

interface TDataGetUsers {
  getSubUsersForAccount: {
    result: Dbx.SubUserAccountsRecord[];
  };
}

interface TVariablesGetUsers {
  id: number;
}

const MUTATION = gql`
  mutation UpdateManySubUserPermissions($clientId: Float!, $input: [UpdateSubUserPermission!]!) {
    updateManySubUserPermissions(client_id: $clientId, input: $input) {
      error {
        code
        message
      }
      result {
        id
        event_managment
        view_and_download
        content_managment
        destinations
        customize_webplayer
        organization_account_info
        activity_log
        billing
        admin_access
        customize_production_notes
        some_location_has_event_managment_enabled
        some_location_has_view_and_download_enabled
        some_location_has_content_managment_enabled
        some_location_has_activity_log_enabled
        some_location_has_customize_production_notes_enabled
      }
    }
  }
`;

export const useUpdateForManySubUserAccount = (): [
  (args: TVariables) => Promise<TData | undefined>,
  MutationResult<TData, 'response'>
] => {
  const [mutation, result] = useMutation<TData, TVariables>(MUTATION);

  const [error, setError] = useState<Error | undefined>();
  const [response, setResponse] = useState<TData | undefined>();

  return [
    useCallback(
      async (args: TVariables): Promise<TData | undefined> => {
        let formatResponse: TData | undefined;

        try {
          const response_ = await mutation({
            variables: {
              clientId: args.clientId,
              input: args.input
            },
            update(cache: ApolloCache<unknown>, { data: fetchData }: FetchResult<TData>): void {
              const dataReadQuery = cache.readQuery<TDataGetUsers, TVariablesGetUsers>({
                query: QUERY,
                variables: {
                  id: args.clientId
                }
              });

              if (dataReadQuery?.getSubUsersForAccount.result) {
                const handleDataReadQuery = dataReadQuery.getSubUsersForAccount.result.map(
                  (subuser) => {
                    if (
                      fetchData?.updateManySubUserPermissions.result.some(
                        (accountPermission) =>
                          accountPermission.id === subuser.subUserPermission?.id
                      )
                    ) {
                      return {
                        ...subuser,
                        subUserPermission: fetchData.updateManySubUserPermissions.result[0]
                      };
                    }

                    return subuser;
                  }
                );

                const reWriteQuery = {
                  getSubUsersForAccount: {
                    ...dataReadQuery.getSubUsersForAccount,
                    result: handleDataReadQuery
                  }
                };

                cache.writeQuery({
                  query: QUERY,
                  data: reWriteQuery
                });
              }
            }
          });

          formatResponse = response_.data ? response_.data : undefined;

          setResponse(formatResponse);
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        return formatResponse;
      },
      [mutation, setResponse]
    ),
    {
      response,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
